/**
 * The Mountaya credential model.
 *
 * There are four credentials, not three, and they compose differently from one another
 * in ways that are easy to get wrong:
 *
 * | Credential   | Header            | Composes with          |
 * |--------------|-------------------|------------------------|
 * | `pk_` key    | `X-API-Key`       | optionally a session   |
 * | `sess_`      | `X-Session-Token` | ACCOMPANIES the key    |
 * | `sk_` secret | `X-API-Key`       | mints sessions only    |
 * | `share_`     | `X-Share-Token`   | REPLACES the key       |
 *
 * A session token does not replace the publishable key: both headers ride together, and
 * sending the session alone is a 401. A share token is the opposite: it replaces the key
 * entirely, reaches exactly the one resource it was minted for, and is refused on every
 * operation whose `security` does not name a share token on its own, every write among them.
 * (A share credential that also asked for cookies reads those operations through the visitor's
 * own session instead; see {@link ShareCredential.cookie}.)
 *
 * The session token names the user an operation acts as. `postSession` mints it for the `user_id`
 * its caller names, or for no user, and every request made with it acts as that user: no header
 * names one. An operation that acts as a member (every `studio-embedded` operation, every
 * `management` write, every read under `/v1/me`) declares `x-acting-user: required`, and the API
 * answers it under a token minted for no user with a 401. A token minted for no user therefore
 * reads and never writes. The token is opaque, so the client knows whom it names only where a
 * {@link SessionTokenSource} reports it, as a `SessionTokenEngine` does from its mint's
 * `user_id`, and refuses such an operation locally there.
 *
 * A token is minted for a surface as well, `postSession`'s `surface`: `api` by default, or
 * `studio-embedded`, the only surface a `studio-embedded` operation accepts, which the API
 * answers under a token minted for another with a 401. The client sends whatever token it is
 * given and lets the API decide: a studio host is the one reader of the surface a token names.
 *
 * A session token lives minutes, so the credential may hold it as a function or as a
 * {@link SessionTokenSource} rather than a string: {@link sessionTokenOf} reads it on every read,
 * and one client follows the token for the page's life. A source also says when a mint is out,
 * and a request made while it holds no token waits for that mint (see `sessionWait.ts`).
 *
 * What an operation takes is read off what it DECLARES, never off its method: whether a secret
 * key authenticates it, whether it takes any credential at all, whether it accepts a share token,
 * whether every way to call it carries a session token, and whether it acts as the user that token
 * names. A generated operation states each from the contract, and a credential that cannot meet them is
 * refused here, before anything is sent: the server would answer the same request with a 401 or
 * a 400 that names none of the rules.
 */

import type { SessionTokenSnapshot } from "../engines/sessionToken.js";
import { describeType, MountayaConfigurationError } from "./errors.js";
import { isEmptyHeaderValue, isHeaderValue } from "./headerValue.js";

/**
 * SessionTokenSource is a session token that changes over time and says when one is on its way:
 * what a `SessionTokenEngine` is, and what `credential.sessionToken` takes beside a string and a
 * function.
 *
 * A request made while the source holds no token and reports `isLoading` subscribes to it for the
 * span of its wait, so an engine nobody holds yet starts its mint, and the request waits for the
 * token, bounded by its `signal` and its `timeoutMs`. Once the wait ends with no token, the
 * request goes out under the key alone, and an operation that requires a session is refused
 * with the mint's `error` as its cause.
 *
 * `isLoading` reports a token on its way, not only a mint out: a source that mints on its next
 * subscriber, as an engine nobody holds yet does, reports it, and a source that reports neither a
 * token nor one on its way is never subscribed by a request. The client's `credentialScope` reads
 * the snapshot by the same rule, so the scope a read is keyed under agrees with what it carries.
 *
 * Whoever holds the source subscribed keeps it fresh: an engine refreshes its token only while it
 * has a listener, and one parked holding a token keeps that token past its expiry. A long-lived
 * client subscribes once for the life of the page or the process.
 *
 * @example
 * ```ts
 * import { Mountaya, SessionTokenEngine } from "@mountaya/client";
 *
 * declare const mintFromMyServer: () => Promise<{
 *   expires_at: Date;
 *   token: string;
 *   user_id: string | null;
 * }>;
 *
 * const session = new SessionTokenEngine({ mint: mintFromMyServer });
 * // The engine refreshes while something subscribes to it: hold it for the process's life.
 * session.subscribe(() => undefined);
 *
 * const mountaya = new Mountaya({
 *   credential: { publishableKey: "pk_…", sessionToken: session },
 * });
 * ```
 */
export interface SessionTokenSource {
  /**
   * The token held now, whether one is on its way, and what the last mint failed with; and, where
   * the source knows them, when the token expires and the user it was minted for.
   */
  getSnapshot(): Pick<SessionTokenSnapshot, "error" | "isLoading" | "token"> & {
    /**
     * When the token held now expires. An event stream opened with it that closes at or after
     * this instant is opened again at once with the next token, as the API ends a stream with
     * its credential.
     */
    readonly expiresAt?: Date | undefined;
    /**
     * The user the token held now was minted for, `null` for none. A call that acts as a member is
     * refused locally while it reads `null`; left out, the call is sent for the API to decide.
     */
    readonly userId?: string | null | undefined;
  };
  /**
   * Registers a listener told of every change of the snapshot, and returns its release. A source
   * may start minting on its first listener, as a `SessionTokenEngine` does.
   */
  subscribe(listener: () => void): () => void;
}

/**
 * PublishableCredential authenticates browser and embedding access with a `pk_` key,
 * optionally scoped up by a session token minted from a secret key.
 *
 * The session token is also who the client acts as: your server mints it with `postSession`'s
 * `user_id` set to the person signed in, and every call that acts as a member acts as that
 * person. A client following a person through sign-in, an account switch and sign-out follows a
 * {@link SessionTokenSource} that mints for whoever is signed in.
 *
 * @example
 * ```ts
 * import { Mountaya } from "@mountaya/client";
 *
 * declare const session: { readonly token: string };
 *
 * // Your server minted `session` with postSession's `user_id` set to the person signed in.
 * const mountaya = new Mountaya({
 *   credential: { publishableKey: "pk_…", sessionToken: session.token },
 * });
 * ```
 */
export interface PublishableCredential {
  /** The `pk_` key, sent as `X-API-Key`. An empty or blank one is refused as a missing credential. */
  readonly publishableKey: string;
  /**
   * The session token that accompanies the key: a `sess_` string, a function the client calls
   * whenever it reads the token, or a {@link SessionTokenSource} such as a `SessionTokenEngine`.
   * The token is read for each request, each event-stream open and each read of
   * `MountayaTransport.mapCredential`, so every map built on the client too.
   *
   * A source is how one client follows a token that rotates and waits for the first one: a
   * request made while the source holds no token and reports one on its way waits for it, so a
   * page's first reads carry the session rather than going out under the key alone. A function may
   * answer `undefined` before the first token is minted, and the key then goes out alone, so an
   * operation that requires a session is refused until it answers one.
   *
   * A string is checked for its `sess_` prefix at construction; what a function or a source
   * answers is checked at every read, where a call made with an answer that fails the check is
   * refused before anything is sent.
   *
   * A token names the user it was minted for: `postSession` mints one for the `user_id` your
   * server gives it, and every request made with it acts as that user. A token minted without
   * `user_id` names nobody, which the reads take and every operation that acts as a member
   * answers with a 401. A source that reports the user each token names, as a
   * `SessionTokenEngine` does, has such an operation refused locally while its token names
   * nobody.
   */
  readonly sessionToken?: string | (() => string | undefined) | SessionTokenSource | undefined;
  /**
   * Never set: a key credential sends no cookie, and one written beside a key is refused at
   * construction. The browser's own session is {@link CookieCredential}, a credential of its
   * own.
   */
  readonly cookie?: undefined;
}

/**
 * SecretCredential authenticates server-to-server session minting with an `sk_` key.
 *
 * It is the only credential that can call `postSession` (`POST /v1/sessions`), and
 * `postSession` is the only operation it authenticates: sent to any other operation that takes a
 * credential, it is refused with a `MountayaConfigurationError` before the request, where the
 * API would answer a 401 as if no credential had been sent. An operation that takes none, such
 * as `postOrganizationLookup`, goes out with nothing the credential holds. It must never reach a
 * browser.
 */
export interface SecretCredential {
  /** The `sk_` key, sent as `X-API-Key` to `postSession` alone. Never quoted in a refusal. */
  readonly secretKey: string;
  /** Never set: see {@link PublishableCredential.cookie}. */
  readonly cookie?: undefined;
}

/**
 * ShareCredential authenticates a link visitor with a `share_` token. It reaches exactly
 * the itinerary or collection the token was minted for, read-only.
 */
export interface ShareCredential {
  /** The `share_` token, sent as `X-Share-Token` in place of a key. */
  readonly shareToken: string;
  /**
   * Also send ambient cookies, on every operation that takes a credential.
   *
   * A share link is often opened by someone who is *also* signed in, and the server answers a
   * signed-in visitor a richer projection than an anonymous one. Sending both lets it, and costs
   * nothing when the visitor is anonymous.
   *
   * It also decides what happens on a read that takes a credential but no share token. Without
   * it the read is refused locally, as the server would refuse the token. With it the token is
   * left off and the read rides the session alone, so a signed-in visitor gets their answer and
   * an anonymous one gets the server's 401. A write is refused either way.
   *
   * @default false
   */
  readonly cookie?: boolean | undefined;
}

/**
 * CookieCredential defers authentication to the browser's ambient credentials.
 *
 * No credential header is sent; the request carries `credentials: "include"` and whatever
 * cookies the browser already holds for the target origin. An operation that takes no
 * credential, such as `postOrganizationLookup`, is the exception: it is sent no cookie. Use it when the caller has already authenticated by other means: a session
 * cookie from your own sign-in, or a backend-for-frontend that fronts the Mountaya API on your
 * origin and attaches the real credential server-side.
 *
 * It is the one credential that carries no secret of its own, which is exactly why it
 * exists: a browser that cannot read its own httpOnly session cookie cannot build a
 * header from it either.
 *
 * **It is outside the published contract, and the local refusals do not apply to it.** The
 * contract's four credentials are the keys and tokens its `security` names; a cookie is
 * Mountaya's own web application's session, or whatever a proxy in front of the API decides it
 * means. So no operation's declaration refuses it: it is not refused where an operation
 * accepts no share token, requires a session token or acts as a member, because the session it
 * rides on already names its user. What the server makes of it is the server's answer.
 *
 * It carries no key and no token. A client given `cookie` beside a key throws
 * `MountayaConfigurationError` at construction, since which of the two was meant is not the
 * client's to guess: `{ cookie: true }` alone rides the browser's session, and a key credential
 * without `cookie` sends its key. Beside a share token, `cookie` is the share credential's own
 * (see {@link ShareCredential.cookie}).
 */
export interface CookieCredential {
  /** Rides the browser's own session: `credentials: "include"` and no credential header. */
  readonly cookie: true;
  /** Never set: the cookie credential carries no key. */
  readonly publishableKey?: undefined;
  /** Never set: the cookie credential carries no key. */
  readonly secretKey?: undefined;
  /** Never set: a session token accompanies a publishable key, which this credential has not. */
  readonly sessionToken?: undefined;
  /** Never set: a share visitor asking for cookies is a {@link ShareCredential} with `cookie`. */
  readonly shareToken?: undefined;
}

/**
 * Credential is any of the ways to authenticate, told apart by which key is present.
 *
 * The union does not stop a caller supplying two: `{ publishableKey, shareToken }` satisfies
 * it, because a union of object types accepts the members of every alternative. So the pair
 * is resolved at runtime, the way the contract says: a share token REPLACES a key beside it.
 * The server, sent both, reads the key and ignores the token, so this client sends the share
 * token alone where the operation accepts one and refuses the call where it does not.
 *
 * A member written as `undefined` is an omitted one, as it is for every client option, so a
 * caller forwarding an optional token keeps the credential the other members make.
 */
export type Credential =
  | PublishableCredential
  | SecretCredential
  | ShareCredential
  | CookieCredential;

const PUBLISHABLE_PREFIX = "pk_";
const SECRET_PREFIX = "sk_";
const SESSION_PREFIX = "sess_";
const SHARE_PREFIX = "share_";

/** The members a credential spells a key or a token with, each of them a string. */
const KEY_MEMBERS = ["publishableKey", "secretKey", "sessionToken", "shareToken"] as const;

/**
 * assertHeaderValue refuses a credential member no request header carries: a line break or
 * another control character inside it, or a character past Latin-1, which a placeholder left in
 * place holds. {@link isHeaderValue} is the rule.
 *
 * Sent as it is, the runtime's `fetch` refuses the request, and the call is reported after its
 * retries as a connection failure that tells the reader to check the network. The refusal names
 * the member and never its value, which may be a key.
 */
const assertHeaderValue = (member: string, value: string): void => {
  if (isHeaderValue(value)) {
    return;
  }

  throw new MountayaConfigurationError(
    `credential.${member} holds a character no request header carries: a line break or another control character inside it, or a character outside Latin-1. ` +
      (value.includes("\u2026")
        ? "It reads as a placeholder: replace it with the value your organization was issued."
        : "Pass it as it was issued."),
  );
};

/**
 * startOf spells the first characters of a value in the publishable key's slot, which is how a
 * reader tells which value landed there. It is never called for the secret key's slot: a value
 * there is a secret until the server says otherwise, and none of it reaches a message.
 */
const startOf = (value: string): string => `Received a value starting with "${value.slice(0, 3)}".`;

/**
 * assertPrefix refuses a key or a token that does not start with the prefix its member takes,
 * naming the member by the path a caller writes it at. `detail` is the sentence that follows the
 * refusal, where the caller has one to add.
 *
 * The prefix is the whole of the check. What follows it is not read, its length least of all:
 * the server alone knows what it issued.
 */
const assertPrefix = (member: string, prefix: string, value: string, detail?: string): void => {
  if (value.startsWith(prefix)) {
    return;
  }

  const refusal = `credential.${member} must start with "${prefix}".`;

  throw new MountayaConfigurationError(detail === undefined ? refusal : `${refusal} ${detail}`);
};

/** The shapes a credential takes, as a refusal spells them for a caller to pick from. */
const CREDENTIAL_SHAPES =
  "{ publishableKey } (with a sessionToken where a call needs one), " +
  "{ shareToken } for a share link, { cookie: true } for the browser's own session, " +
  "or { secretKey } on a server";

/**
 * memberOf reads one member of a credential as the value it holds, `undefined` where it is
 * absent or written as `undefined`.
 */
const memberOf = (credential: Credential, member: string): unknown =>
  Reflect.get(credential, member);

/**
 * holdsKey reports whether a credential carries a key or a token in `member`. A string that is
 * empty or whitespace alone carries none, since that is how an unset environment variable
 * arrives: the credential is then read as one without it, and refused as a missing one is.
 */
const holdsKey = (credential: Credential, member: (typeof KEY_MEMBERS)[number]): boolean => {
  const value = memberOf(credential, member);

  return value !== undefined && !(typeof value === "string" && isEmptyHeaderValue(value));
};

/**
 * isSecretCredential reports whether a credential carries a secret key, which then decides its
 * kind whatever else it carries.
 */
export const isSecretCredential = (credential: Credential): credential is SecretCredential =>
  holdsKey(credential, "secretKey");

/**
 * isShareCredential reports whether a credential carries a share token, which then replaces a
 * publishable key beside it.
 */
export const isShareCredential = (credential: Credential): credential is ShareCredential =>
  holdsKey(credential, "shareToken");

/** isPublishableCredential reports whether a credential carries a publishable key. */
export const isPublishableCredential = (
  credential: Credential,
): credential is PublishableCredential => holdsKey(credential, "publishableKey");

/**
 * isCookie reports whether a credential is the browser's own session: `cookie: true` with no key
 * or token beside it. `cookie` beside a key makes no cookie credential, and
 * {@link assertCredential} refuses it.
 */
const isCookie = (credential: Credential): credential is CookieCredential =>
  memberOf(credential, "cookie") === true &&
  KEY_MEMBERS.every((member) => !holdsKey(credential, member));

/**
 * isSessionTokenSource reports whether a session token member is a {@link SessionTokenSource}: an
 * object whose `getSnapshot` and `subscribe` are functions.
 */
const isSessionTokenSource = (value: unknown): value is SessionTokenSource =>
  typeof value === "object" &&
  value !== null &&
  typeof Reflect.get(value, "getSnapshot") === "function" &&
  typeof Reflect.get(value, "subscribe") === "function";

/**
 * assertSourceMembers refuses a session token object that states `getSnapshot` or `subscribe`
 * and is still no {@link SessionTokenSource}, naming the member that is no function by its type
 * and never its value. An object that states neither is left to the refusal of its type.
 */
const assertSourceMembers = (value: unknown): void => {
  if (typeof value !== "object" || value === null) {
    return;
  }

  if (!("getSnapshot" in value) && !("subscribe" in value)) {
    return;
  }

  for (const name of ["getSnapshot", "subscribe"]) {
    const found: unknown = Reflect.get(value, name);

    if (typeof found !== "function") {
      throw new MountayaConfigurationError(
        `credential.sessionToken.${name} is ${describeType(found)}; a session token source ` +
          "states getSnapshot and subscribe as functions, as a SessionTokenEngine does.",
      );
    }
  }
};

/**
 * assertMemberTypes refuses a credential member of a type no credential takes: a key or a token
 * that is not a string, a session token that is neither a string, a function nor a
 * {@link SessionTokenSource}, or a `cookie` that is not a boolean. A member that is a string is
 * held to what a request header carries, by {@link assertHeaderValue}.
 *
 * The refusal names the member and its type and never its value, since a key is a secret
 * whatever type it arrived as. A session token's function is not called here: what it answers
 * is checked where it is read, by {@link sessionTokenOf}.
 */
const assertMemberTypes = (credential: Credential): void => {
  for (const member of KEY_MEMBERS) {
    const value = memberOf(credential, member);
    const readPerRequest =
      member === "sessionToken" && (typeof value === "function" || isSessionTokenSource(value));

    if (member === "sessionToken" && !readPerRequest) {
      assertSourceMembers(value);
    }

    if (value !== undefined && typeof value !== "string" && !readPerRequest) {
      throw new MountayaConfigurationError(
        `credential.${member} is ${describeType(value)}; pass it as a string, ` +
          (member === "sessionToken" ? "a function answering one, or a SessionTokenEngine, " : "") +
          "or leave it out.",
      );
    }

    if (typeof value === "string") {
      if (member === "sessionToken" && isEmptyHeaderValue(value)) {
        throw new MountayaConfigurationError(
          `credential.sessionToken is ${value === "" ? "an empty string" : "whitespace alone"}; pass the token your server minted, or leave it out.`,
        );
      }

      assertHeaderValue(member, value);
    }
  }

  const cookie = memberOf(credential, "cookie");
  if (cookie !== undefined && typeof cookie !== "boolean") {
    throw new MountayaConfigurationError(
      `credential.cookie is ${describeType(cookie)}; pass true to send the browser's own cookies, or leave it out.`,
    );
  }
};

/**
 * refuseCookieBesideKey builds the refusal for `cookie` written beside a key: which of the two
 * the caller meant is not the client's to guess, and either reading is wrong for the caller who
 * meant the other.
 */
const refuseCookieBesideKey = (key: "publishableKey" | "secretKey"): MountayaConfigurationError =>
  new MountayaConfigurationError(
    `credential carries ${key} and cookie together, which no credential does: a cookie ` +
      "credential carries no key, and a key credential sends no cookie. Drop cookie to " +
      "authenticate with the key, or pass { cookie: true } alone to ride the browser's own " +
      "session.",
  );

/**
 * wantsCookies reports whether a credential asks for ambient browser credentials.
 *
 * Only the cookie and share credentials ever do. A publishable key deliberately does NOT:
 * sending a cookie alongside a scoped key lets the cookie's broader session outrank the
 * key, so an embed would silently see more than the key permits. A request made under a
 * credential this answers false for carries `credentials: "omit"`, so no cookie travels with it
 * even where the API is reached through the page's own origin.
 */
export const wantsCookies = (credential: Credential): boolean =>
  isCookie(credential) || (isShareCredential(credential) && credential.cookie === true);

/**
 * inShippedRuntime reports whether the current runtime is one whose code ships to the people who
 * use it: a page, whose global object holds both a DOM `window` and a `document`, or a React
 * Native application, whose `navigator.product` reads `ReactNative`. Anyone holding the
 * application reads every value bundled into it, a secret key among them.
 *
 * This is the one place the package looks at `window`, `document` or `navigator`, and it looks
 * at them to refuse work rather than to do any. `globalThis` is used rather than a bare `window`
 * reference so the check stays a runtime question in every bundler, and so it never throws under
 * Node where the identifier is simply absent.
 *
 * It reads those members and nothing else of the runtime. A browser's worker (dedicated, shared
 * or service) holds neither a `window` nor a `document`, and its `navigator.product` reads
 * `Gecko`, so it answers false there and the guard does not reach a worker's bundle: what keeps a
 * secret key out of one is the browser entry point, which takes no secret key at all. Node's own
 * `navigator` names no product.
 */
const inShippedRuntime = (): boolean => {
  if (typeof globalThis !== "object") {
    return false;
  }

  if ("window" in globalThis && "document" in globalThis) {
    return true;
  }

  // Read against a widened type: `lib.dom` declares a `navigator` on every runtime, and a runtime
  // may hold none, or one whose `product` is no string.
  const runtime: { readonly navigator?: { readonly product?: unknown } | undefined } = globalThis;

  return runtime.navigator?.product === "ReactNative";
};

/**
 * assertSessionToken refuses a session token no request header carries and one that does not
 * start with `sess_`: the checks a string gets at construction and a function's answer gets at
 * every read.
 *
 * The refusal never quotes the value, which may be a key pasted into the wrong slot.
 */
const assertSessionToken = (token: string): void => {
  assertHeaderValue("sessionToken", token);
  // The value's own first characters stay out of this one: a key pasted here is the likely slip.
  assertPrefix("sessionToken", SESSION_PREFIX, token);
};

/**
 * sessionTokenOf reads the session token a publishable credential carries at this moment: the
 * string it holds, what its function answers now, or the token its source's snapshot holds, with
 * `undefined` for none.
 *
 * Every reader of the token goes through it (the headers of a request, an event-stream open, the
 * projection a map reads), so a function or a source is read once per read and its answer is
 * never kept. A string was checked at construction and is returned as it is.
 *
 * @throws `MountayaConfigurationError` where the function or the source answers neither a string
 *   nor `undefined`, naming the type and never the value, or a string without the `sess_`
 *   prefix, or one no request header carries.
 */
export const sessionTokenOf = (credential: PublishableCredential): string | undefined => {
  const member = credential.sessionToken;
  if (member === undefined || typeof member === "string") {
    return member;
  }

  // Typed as the credential says, and still whatever the caller's function or source answered:
  // read as unknown until it is shown to be a string.
  const answer: unknown = typeof member === "function" ? member() : member.getSnapshot().token;
  if (answer === undefined) {
    return undefined;
  }

  if (typeof answer !== "string") {
    throw new MountayaConfigurationError(
      `credential.sessionToken answered ${describeType(answer)}; answer the current token as a ` +
        "string, or undefined while there is none.",
    );
  }

  assertSessionToken(answer);

  return answer;
};

/**
 * sessionSourceOf answers the {@link SessionTokenSource} a credential holds as its session token,
 * or `undefined` where it holds a string, a function or none.
 */
export const sessionSourceOf = (credential: Credential): SessionTokenSource | undefined => {
  const member = memberOf(credential, "sessionToken");

  return isPublishableCredential(credential) && isSessionTokenSource(member) ? member : undefined;
};

/**
 * sessionUserOf reads the user the session token a credential holds now was minted for, as its
 * source reports it: a string names one, `null` names none, and undefined is a source that does
 * not say, or a token held as a string or a function, whose user the client is not told.
 */
export const sessionUserOf = (credential: Credential): string | null | undefined => {
  const source = sessionSourceOf(credential);
  if (source === undefined) {
    return undefined;
  }

  // Read as unknown: a source of the caller's own may answer anything there.
  const user: unknown = source.getSnapshot().userId;

  return typeof user === "string" || user === null ? user : undefined;
};

/**
 * sessionExpiryOf reads when the session token a credential holds now expires, as its source
 * reports it, or undefined where it reports no instant.
 */
export const sessionExpiryOf = (credential: Credential): Date | undefined => {
  const expiry: unknown = sessionSourceOf(credential)?.getSnapshot().expiresAt;

  return expiry instanceof Date && !Number.isNaN(expiry.getTime()) ? expiry : undefined;
};

/**
 * holdsSession reports whether a request made now under a publishable credential carries a
 * session token or waits for one: a string, a function answering a string now, or a source that
 * holds a token or reports one on its way, which is the rule a request's wait reads too. It
 * never quotes the token, and an answer that is not a token is refused where a request reads it,
 * not here.
 *
 * @throws what a session token held as a function throws, since the function is called here.
 */
export const holdsSession = (credential: PublishableCredential): boolean => {
  const member = credential.sessionToken;
  if (member === undefined || typeof member === "string") {
    return member !== undefined;
  }

  if (typeof member === "function") {
    return typeof member() === "string";
  }

  const snapshot = member.getSnapshot();

  return snapshot.token !== undefined || snapshot.isLoading;
};

/**
 * assertCredential rejects a credential that cannot work, before any request is made.
 *
 * Prefix checking is not pedantry. Every one of these values is an opaque string, so a
 * publishable key pasted into the secret slot produces a 401 from the server with no hint
 * about which of the two was wrong, and the mistake is common, because the two are
 * copied from adjacent rows of the same settings page.
 *
 * The browser guard is the second of two defences against leaking a secret key into a
 * client bundle. The first is that `@mountaya/client/browser` never exports the secret
 * constructor at all, so a correctly-resolved browser build cannot reach it. This one
 * catches the case that entry point cannot: a bundler resolving the Node condition into
 * code that ships to a browser anyway, or a React Native application, which resolves no
 * browser entry point at all.
 *
 * The shape is checked before any prefix, because a caller outside the compiler (a JavaScript
 * caller, or a value past a cast) can hand over anything: no credential at all, a member of the
 * wrong type, `cookie` beside a key, or an object that is none of the shapes a credential takes.
 * Each is refused here, named, rather than read as the nearest shape or thrown as a `TypeError`
 * from inside the check.
 *
 * @throws `MountayaConfigurationError` for every credential that cannot work.
 */
export const assertCredential = (credential: Credential): void => {
  // Typed as the union, and still whatever the caller passed: read as unknown until it is shown
  // to be an object.
  const value: unknown = credential;
  if (typeof value !== "object" || value === null) {
    throw new MountayaConfigurationError(
      `credential is ${describeType(value)}; pass ${CREDENTIAL_SHAPES}.`,
    );
  }

  assertMemberTypes(credential);

  if (isSecretCredential(credential)) {
    if (memberOf(credential, "cookie") !== undefined) {
      throw refuseCookieBesideKey("secretKey");
    }

    assertPrefix(
      "secretKey",
      SECRET_PREFIX,
      credential.secretKey,
      credential.secretKey.startsWith(PUBLISHABLE_PREFIX)
        ? "This looks like a publishable key: pass it as { publishableKey }."
        : // Nothing of the value is quoted: a key pasted without its prefix is still a secret.
          "Pass the secret key as it was issued: nothing of the value is quoted here.",
    );

    if (inShippedRuntime()) {
      throw new MountayaConfigurationError(
        "A secret key must never reach a browser or a mobile application: anyone can read it out of the bundle. " +
          "Mint a session token on your server with POST /v1/sessions and send that to the client instead.",
      );
    }

    return;
  }

  // A share token REPLACES a key beside it, so the pair is a share credential, and `cookie`
  // beside the token is how a signed-in visitor asks for their own session too.
  if (isShareCredential(credential)) {
    // Nothing of the value is quoted: a share token opens a resource to whoever holds it.
    assertPrefix("shareToken", SHARE_PREFIX, credential.shareToken);

    return;
  }

  if (isPublishableCredential(credential)) {
    if (memberOf(credential, "cookie") !== undefined) {
      throw refuseCookieBesideKey("publishableKey");
    }

    assertPrefix(
      "publishableKey",
      PUBLISHABLE_PREFIX,
      credential.publishableKey,
      credential.publishableKey.startsWith(SECRET_PREFIX)
        ? "This looks like a secret key: pass it as { secretKey }, and only from a server."
        : startOf(credential.publishableKey),
    );

    // A string is checked now; a function or a source has nothing to answer before its first
    // mint, so its answer is checked at every read instead.
    if (typeof credential.sessionToken === "string") {
      assertSessionToken(credential.sessionToken);
    }

    return;
  }

  if (memberOf(credential, "cookie") !== undefined) {
    // A key an unset environment variable left blank still says a key was meant: read as the
    // browser's own session, the credential would send the visitor's cookies in its place.
    const blank = KEY_MEMBERS.find(
      (member) => member !== "sessionToken" && memberOf(credential, member) !== undefined,
    );
    if (blank !== undefined) {
      throw new MountayaConfigurationError(
        `credential carries cookie beside an empty ${blank}, which is how an unset environment ` +
          `variable arrives: set ${blank}, or pass { cookie: true } alone to ride the browser's ` +
          "own session.",
      );
    }
  }

  if (isCookie(credential)) {
    return;
  }

  // None of the shapes a credential takes. A key member still written here is blank, since a key
  // that holds text makes a shape above, and is named so the unset variable behind it is found.
  const blanks = KEY_MEMBERS.filter(
    (member) => member !== "sessionToken" && memberOf(credential, member) !== undefined,
  ).map((member) => `credential.${member}`);
  const blankNote =
    blanks.length === 0
      ? ""
      : ` (${blanks.join(" and ")} ${blanks.length === 1 ? "is" : "are"} empty or blank, which ` +
        "is how an unset environment variable arrives, and counts as none)";

  // A session token alone is the likeliest slip, and the one whose fix can be spelled out exactly.
  throw new MountayaConfigurationError(
    memberOf(credential, "sessionToken") === undefined
      ? `credential carries no publishableKey, secretKey, shareToken or cookie: true${blankNote}, so it authenticates nothing; pass ${CREDENTIAL_SHAPES}.`
      : "credential carries a sessionToken and no publishableKey, which the session token accompanies rather than replaces; pass { publishableKey, sessionToken }.",
  );
};

/**
 * OperationCredentials is what one operation declares about the credentials it takes, plus the
 * method it is sent with: everything the local refusals below read.
 *
 * A generated operation states each flag from the contract, and `RequestArgs` carries the same
 * members, so a request descriptor IS one of these. Every credential flag is read as declared
 * and none is inferred from the method: an absent flag is an operation that does not declare the
 * thing it names, so a share token is refused, a session token is not required, and a secret key
 * is refused unless the operation takes no credential.
 */
export interface OperationCredentials {
  /**
   * Whether one of the operation's security requirements is a share token on its own. Where it
   * is not, a share token is refused locally, which is how every write refuses one, and how a
   * read no share link reaches refuses one before the 401 the server would answer.
   */
  readonly acceptsShareToken?: boolean | undefined;
  /**
   * Whether the operation acts as the user its session token was minted for: its
   * `x-acting-user` is `required`. Where it does, a credential whose session source reports a
   * token minted for no user is refused locally, since the API would answer the call with a 401.
   */
  readonly actsAsMember?: boolean | undefined;
  /** The HTTP method, which a refusal names where the call names no operation. */
  readonly method: string;
  /** Names the operation in a refusal, so the message says which call was refused. */
  readonly operationId?: string | undefined;
  /**
   * Whether the operation only reads, for a read the method would call a write: a share token
   * refused on a write is told it writes.
   */
  readonly read?: boolean | undefined;
  /**
   * Whether the operation is authenticated by a secret key, which `postSession` alone is. Where
   * it is, every credential but a secret key is refused locally; where it is not, a secret key
   * is refused, unless the operation takes no credential.
   */
  readonly requiresSecretKey?: boolean | undefined;
  /**
   * Whether every one of the operation's security requirements names a session token. Where it
   * does, a credential carrying none is refused locally rather than sent to be answered with a
   * 401.
   */
  readonly requiresSession?: boolean | undefined;
  /**
   * Whether the operation takes no credential at all, as `postOrganizationLookup` does. Where it
   * does, nothing the credential holds is sent and nothing is refused.
   */
  readonly takesNoCredential?: boolean | undefined;
}

/** The operation a refusal names, or its method where the call names no operation. */
const subjectOf = (operation: Pick<OperationCredentials, "method" | "operationId">): string =>
  operation.operationId ?? `A ${operation.method.toUpperCase()}`;

/**
 * Reports whether an operation writes: sent with any method but a read's, and not declared a
 * read, which every Data API query is over its POST.
 */
const isWrite = (operation: Pick<OperationCredentials, "method" | "read">): boolean => {
  const upper = operation.method.toUpperCase();

  return operation.read !== true && upper !== "GET" && upper !== "HEAD" && upper !== "OPTIONS";
};

/**
 * credentialHeaders projects a credential onto the headers for one operation, refusing locally
 * a credential that operation cannot take.
 *
 * The refusals, each read off the operation's DECLARATION and never off its method, and each
 * one a request the server would answer with a status that names no rule:
 *
 * - **Any credential but a secret key where a secret key authenticates the operation**, which
 *   `postSession` alone declares. The server answers a publishable key there with a 401 that
 *   says only that the credential is not accepted.
 * - **A secret key on any other operation that takes a credential.** The server does not read
 *   it there at all, and answers with a 401 as if nothing had been sent.
 * - **A share token where the operation accepts none.** The server answers a share-token write
 *   with a 401, as if no credential had been sent, or a 400 when the body fails validation
 *   first; neither says the token cannot write. It REPLACES a key beside it, so a client
 *   configured with both sends the share token alone, and is refused where it cannot be sent.
 * - **No session token where every requirement names one.** A share token carries none; a
 *   publishable key carries one only when it was given one, or while its function answers one.
 * - **A session token minted for no user where the operation acts as a member.** Read off a
 *   {@link SessionTokenSource} that reports the user, as a `SessionTokenEngine` does; a token held
 *   as a string or a function is sent, since the client is not told whom it names.
 *
 * An operation that takes no credential, as `postOrganizationLookup` does, is read against none
 * of them and sent nothing the credential holds.
 *
 * A {@link CookieCredential} is read against none of them either: it is outside the published
 * contract, and its session already names its user. It sends no header of its own.
 *
 * A {@link ShareCredential} that asked for cookies is that credential on a read that takes no
 * share token: nothing is refused and no header is sent, so the visitor's own session carries
 * the read if they have one and the server answers a 401 if they do not. This is how a visitor
 * who is also signed in reads beyond the link, their own itineraries for one. A write is refused
 * all the same, since the credential is a read-only link whatever rides with it.
 */
export const credentialHeaders = (
  credential: Credential,
  operation: OperationCredentials,
): Record<string, string> => {
  if (operation.takesNoCredential === true) {
    return {};
  }

  if (isCookie(credential)) {
    // No header by design. The browser attaches the cookie; a header built from one it
    // cannot read is the thing this credential exists to avoid.
    return {};
  }

  if (operation.requiresSecretKey === true) {
    if (isSecretCredential(credential)) {
      return { "X-API-Key": credential.secretKey };
    }

    throw new MountayaConfigurationError(
      `${subjectOf(operation)} is authenticated by a secret key alone, which must never reach a ` +
        "browser. Call it on your server with { secretKey }, and give the caller the session " +
        "token it mints as { publishableKey, sessionToken }.",
    );
  }

  if (isSecretCredential(credential)) {
    throw new MountayaConfigurationError(
      `${subjectOf(operation)} does not take a secret key: a secret key authenticates ` +
        "postSession alone, and the API answers one here with a 401 as if nothing had been " +
        "sent. " +
        (operation.requiresSession !== true
          ? "Call this one with a publishable key, and keep the secret key on the server that " +
            "mints sessions."
          : operation.actsAsMember === true
            ? "Mint a session token with it on your server, giving postSession's user_id the " +
              "member this call acts as, and call this one with { publishableKey, sessionToken }."
            : "Mint a session token with it on your server, and call this one with " +
              "{ publishableKey, sessionToken }."),
    );
  }

  if (isShareCredential(credential)) {
    if (operation.acceptsShareToken === true) {
      return { "X-Share-Token": credential.shareToken };
    }

    if (credential.cookie === true && !isWrite(operation)) {
      // The visitor's own session carries this read, as the cookie the browser attaches, so
      // no header is sent: the token stays off an operation that does not declare it, and
      // whether a session is there to answer is the server's to say.
      return {};
    }

    throw new MountayaConfigurationError(
      `${subjectOf(operation)} does not accept a share token. A share token is read-only and ` +
        "reaches the one itinerary, collection or zone it was minted for, through the " +
        "operations that declare it. " +
        (isWrite(operation)
          ? "Minting, rotating and disabling a share link take a publishable key with the " +
            "`studio-embedded` scope and a session token minted for `studio-embedded` naming a " +
            "member with a write role."
          : "Call this one with a publishable key."),
    );
  }

  // Read once for this request: a function is called here, and its answer is what both the
  // session requirement and the header below read.
  const sessionToken = sessionTokenOf(credential);

  if (operation.requiresSession === true && sessionToken === undefined) {
    // A call that acts as a member is told which token it needs as well as that it needs one:
    // the API reads the member off the token, so a token minted for nobody is refused there as
    // surely as none.
    const forMember =
      operation.actsAsMember === true
        ? " This call acts as a member, so mint the token with postSession's user_id set to the " +
          "user it acts as."
        : "";
    const source = sessionSourceOf(credential);
    if (source !== undefined) {
      const { error } = source.getSnapshot();

      throw new MountayaConfigurationError(
        `${subjectOf(operation)} requires a session token beside the publishable key, and ` +
          "credential.sessionToken holds none: " +
          (error === undefined
            ? "no token has been minted, or minting is disabled. "
            : "its last mint failed, which this error carries as its cause. ") +
          `Make the call once it holds the token your server minted.${forMember}`,
        error === undefined ? undefined : { cause: error },
      );
    }

    if (typeof credential.sessionToken === "function") {
      throw new MountayaConfigurationError(
        `${subjectOf(operation)} requires a session token beside the publishable key, and ` +
          "credential.sessionToken answered none: no token has been minted yet, or the " +
          `session has ended. Make the call once it answers the token your server minted.${forMember}`,
      );
    }

    throw new MountayaConfigurationError(
      operation.actsAsMember === true
        ? `${subjectOf(operation)} acts as a member, so it requires a session token minted for ` +
            "that member beside the publishable key. Mint one on your server with POST " +
            "/v1/sessions, giving user_id the user it acts as, and pass " +
            "{ publishableKey, sessionToken } when constructing the client."
        : `${subjectOf(operation)} requires a session token beside the publishable key. Mint ` +
            "one on your server with POST /v1/sessions and pass { publishableKey, sessionToken } " +
            "when constructing the client.",
    );
  }

  // The token names the user the call acts as, and a source that reports whom says so: one
  // minted for nobody reads and never writes, and the API answers this call under it with a 401
  // that names no rule. A token held as a string or a function names a user the client is not
  // told, and the call goes out for the API to decide.
  if (
    operation.actsAsMember === true &&
    sessionToken !== undefined &&
    sessionUserOf(credential) === null
  ) {
    throw new MountayaConfigurationError(
      `${subjectOf(operation)} acts as the user its session token was minted for, and ` +
        "credential.sessionToken holds a token minted for no user, which reads and never acts as " +
        "a member. Mint the token on your server with postSession's user_id set to the user " +
        "signed in.",
    );
  }

  const headers: Record<string, string> = { "X-API-Key": credential.publishableKey };

  // The session token accompanies the key rather than replacing it. Sending it alone is
  // a 401, which is why this is an addition and never an else-branch.
  if (sessionToken !== undefined) {
    headers["X-Session-Token"] = sessionToken;
  }

  return headers;
};
